<template>
    <div class="m-cart__payment-status">
        <div
            class="message-card"
            :class="isStatus('success') ? 'message-card--success' : 'message-card--error'"
        >
            <header class="message-card__header">
                <img
                    class="logo"
                    :src="$settings('site_logos.header.link')"
                    :alt="$settings('site_title.site_title')"
                >
                <div class="transaction-info">
                    <div class="top">
                        <span class="name">
                            {{ $t('cart.payment.dear_user', [ $store.getters['auth/user'].name_full ]) }}
                        </span>
                        <div class="date">
                            {{ nowDate }}
                        </div>
                    </div>
                    <div class="bottom">
                        <p class="message">
                            <span class="icon-background">
                                <y-icon
                                    :icon="isStatus('success') ? 'check' : 'times'"
                                    color="white"
                                    size="xtiny"
                                />
                            </span>
                            <span class="text">
                                {{ $t(`cart.payment.status.${isStatus('success') ? 'success_msg' : 'error_msg'}`) }}
                            </span>
                        </p>
                        <div
                            v-if="$route.query.tracking_number"
                            class="code"
                        >
                            {{ `${$t('cart.payment.status.tracking_number')}:` }}
                            <strong>{{ digits($route.query.tracking_number, $i18n.locale) }}</strong>
                        </div>
                    </div>
                </div>
            </header>
            <div class="message-card__body">
                <template v-if="isStatus('error')">
                    <h5 class="title">
                        {{ $t('cart.payment.status.error') }}
                    </h5>
                    <p class="description">
                        {{ $t('cart.payment.status.return_money') }}
                    </p>
                    <div class="buttons d-flex align-items-center justify-content-center mt-8">
                        <y-button
                            tag="router-link"
                            :disabled="disableButtons || $wait.is('loading-repayment-button')"
                            color="white"
                            flat
                            class="ml-7"
                            :to="{ name: 'home' }"
                        >
                            {{ $t('button.cancel') }}
                        </y-button>
                        <y-button
                            loading-on="loading-repayment-button"
                            :disabled="disableButtons"
                            color="primary"
                            flat
                            @click.prevent="paymentTransaction(repaymentModelKey, orderModel)"
                        >
                            {{ $t('cart.payment.repayment') }}
                        </y-button>
                    </div>
                </template>
                <template v-else>
                    <y-loading
                        :active="$wait.is('fetch-order-details')"
                        position="relative"
                        height="315px"
                    >
                        <!-- Details -->
                        <div
                            v-if="orderDetails"
                            class="orders-details"
                        >
                            <div class="orders-details__items">
                                <div
                                    v-for="item in orderDetails.items"
                                    :key="item.id"
                                    class="orders-details__item d-flex align-items-center justify-content-between px-8 py-2 mb-2"
                                >
                                    <div class="d-flex align-items-center justify-content-start">
                                        <y-image
                                            :src="item.post_image.versions.medium"
                                            width="64px"
                                            ratio="1"
                                            class="ml-4"
                                        />
                                        <div class="d-flex flex-column align-items-start justify-content-center">
                                            <span class="typo-subtitle-2">
                                                {{ item.ware_title }}
                                            </span>
                                            <span class="typo-subtitle-2 color-body-text--light-40">
                                                {{ `${digits(item.count, $i18n.locale)} ${item.ware_unit}` }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="d-flex flex-column align-items-end">
                                        <span
                                            v-if="item.total_price !== item.payable_price"
                                            class="typo-subtitle-2 color-body-text--light-40"
                                        >
                                            <y-badge
                                                :content="`${digits(percent(item.total_price, item.payable_price), $i18n.locale)}%`"
                                                color="primary"
                                                size="small"
                                            />
                                            <span class="total-price color-body-text--light-60">
                                                {{ $t('cart.invoice.price_unit', [separated(item.total_price, $i18n.locale)]) }}
                                            </span>
                                        </span>
                                        <span class="typo-subtitle-2">
                                            {{ $t('cart.invoice.price_unit', [separated(item.payable_price, $i18n.locale)]) }}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class="orders-details__footer pt-4">
                                <div class="top">
                                    <div class="right">
                                        <p class="tracking-description">
                                            {{ $t('cart.payment.tracking_description') }}
                                        </p>
                                    </div>
                                    <div class="left">
                                        <div class="mb-4">
                                            <span class="typo-body-2">
                                                {{ `${$t('cart.payment.resources.tracking_number')}:` }}
                                            </span>
                                            <span class="typo-body-1">
                                                {{ digits(orderDetails.tracking_number, $i18n.locale) }}
                                            </span>
                                        </div>
                                        <div class="mb-4">
                                            <span class="typo-body-2">
                                                {{ `${$t('cart.payment.resources.shipping_price')}:` }}
                                            </span>
                                            <span class="typo-body-1">
                                                {{ costShipping(orderDetails.delivery_price) }}
                                            </span>
                                        </div>
                                        <div class="mb-10 pb-10">
                                            <span class="typo-body-2">
                                                {{ `${$t('cart.payment.resources.sum_price')}:` }}
                                            </span>
                                            <span class="typo-h5 color-success-text">
                                                {{ $t('cart.invoice.price_unit', [separated(orderDetails.payable_amount, $i18n.locale)]) }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-10 w-100">
                                    <y-button
                                        block
                                        flat
                                        color="primary"
                                        :content="$t('cart.payment.order_tracking')"
                                        tag="router-link"
                                        :to="{ name: 'dashboard-order-details', params: { id: orderModel.id }}"
                                    />
                                </div>
                            </div>
                        </div>
                    </y-loading>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    // Mixins
    import PageMixin from '@nodes/mixins/Page';
    import CartMixin from '@nodes/mixins/Cart';
    import { digits, separated, percent } from '@nodes/helpers/number';
    import { date, currentDate } from '@nodes/helpers/date';
    import { YIcon, YImage, YBadge } from '@components';

    export default {

        name: 'Payment',

        components: {
            YIcon,
            YImage,
            YBadge,
        },

        mixins: [PageMixin, CartMixin],

        /**
         * @inheritDoc
         */
        data() {
            return {
                pageTitle        : this.$t('cart.payment.title'),
                repaymentModelKey: this.$config('cart.repayment_model_key'),
                orderModel       : null,
                disableButtons   : false,
                orderDetails     : null,
            };
        },

        computed: {
            /**
             * Return now time
             * 
             * @returns {string | number}
             */
            nowDate() {
                return date(currentDate(), 'YYYY/MM/DD', this.$i18n.locale);
            },
        },

        /**
         * @inheritDoc
         */
        mounted() {
            this.orderModel = JSON.parse(localStorage.getItem(this.repaymentModelKey));
            if (this.isStatus('success')) {
                this.fetchOrderDetails();
            }
        },

        methods: {
            digits,

            separated,

            percent,

            /**
             * Return status of payment.
             * 
             * @param {string} slug - slug of status
             * @returns {boolean}
             */
            isStatus(slug) {
                return this.$route.query[slug] > 0;
            },

            /**
             * Custom Payment Transaction.
             */
            afterSuccessPaymentTransaction() {
                this.disableButtons = true;
            },

            /**
             * Return Cost shipping value
             *
             * @param {number|undefined} value
             * @returns {number | string}
             */
            costShipping(value) {
                if (value === undefined) {
                    return this.$t('cart.shipping_list.undefined');
                }
                if (value === 0) {
                    return this.$t('cart.shipping_list.free');
                }
                return this.$t('cart.invoice.price_unit', [this.separated(value, this.$i18n.locale)]);
            },

            /**
             * Fetch Order Details
             * 
             * @returns {*}
             */
            fetchOrderDetails() {
                this.$wait.start('fetch-order-details');
                const params = {
                    id       : this.orderModel?.id,
                    including: [
                        'transactions', 'items', 'effected_at',
                        'tracking_number', 'shipping_details',
                        'total_price', 'payable_amount',
                        'delivery_price',
                    ],
                };
                return this.$services.Cart.orderDetails(params).then((response) => {
                    this.orderDetails = response.data.results;
                    this.$store.dispatch('cart/emptyCart');
                    localStorage.removeItem(this.repaymentModelKey);
                }).catch((error) => {
                    this.handleError(error);
                }).finally(() => {
                    this.$wait.end('fetch-order-details');
                });
            },
        },
    };
</script>
